import React from 'react';
import {
  TextField,
  Box,
  Typography,
  Alert,
  Divider,
  Button,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
} from '@mui/material';
import { ClearAll } from '@mui/icons-material';

interface BidsData {
  bid1Description: string;
  bid1Total: string;
  bid2Description: string;
  bid2Total: string;
  bid3Description: string;
  bid3Total: string;
  isLowestBid: string;
  selectionReason: string;
}

interface BidsFormProps {
  data: BidsData;
  onChange: (data: BidsData) => void;
  disabled?: boolean;
  errors?: { field: string; message: string }[];
}

const BidsForm: React.FC<BidsFormProps> = ({ data, onChange, disabled = false, errors = [] }) => {
  const getFieldError = (fieldName: string) => {
    return errors.find(err => err.field === fieldName)?.message;
  };
  const handleChange = (field: keyof BidsData) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    onChange({
      ...data,
      [field]: event.target.value,
    });
  };

  const handleClear = () => {
    onChange({
      bid1Description: '',
      bid1Total: '',
      bid2Description: '',
      bid2Total: '',
      bid3Description: '',
      bid3Total: '',
      isLowestBid: '',
      selectionReason: '',
    });
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">D. Third Party Qualification and Selection: Competitive Bidding</Typography>
        <Button
          variant="outlined"
          color="secondary"
          startIcon={<ClearAll />}
          onClick={handleClear}
          size="small"
        >
          Clear Form
        </Button>
      </Box>

      <Typography variant="body2" sx={{ mb: 3 }}>
        List each third party, total cost, and give a description about the bid (quantitative and qualitative assessment):
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
            Bid 1
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Description (quantitative and qualitative assessment)"
              value={data.bid1Description}
              onChange={handleChange('bid1Description')}
              variant="outlined"
              disabled={disabled}
              error={!!getFieldError('bid1Description')}
              helperText={getFieldError('bid1Description')}
            />
            <TextField
              sx={{ width: '200px' }}
              label="Bid Total (USD)"
              value={data.bid1Total}
              onChange={handleChange('bid1Total')}
              variant="outlined"
              disabled={disabled}
              error={!!getFieldError('bid1Total')}
              helperText={getFieldError('bid1Total')}
            />
          </Box>
        </Box>

        <Divider />

        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
            Bid 2
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Description (quantitative and qualitative assessment)"
              value={data.bid2Description}
              onChange={handleChange('bid2Description')}
              variant="outlined"
              disabled={disabled}
              error={!!getFieldError('bid2Description')}
              helperText={getFieldError('bid2Description')}
            />
            <TextField
              sx={{ width: '200px' }}
              label="Bid Total (USD)"
              value={data.bid2Total}
              onChange={handleChange('bid2Total')}
              variant="outlined"
              disabled={disabled}
              error={!!getFieldError('bid2Total')}
              helperText={getFieldError('bid2Total')}
            />
          </Box>
        </Box>

        <Divider />

        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
            Bid 3
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Description (quantitative and qualitative assessment)"
              value={data.bid3Description}
              onChange={handleChange('bid3Description')}
              variant="outlined"
              disabled={disabled}
            />
            <TextField
              sx={{ width: '200px' }}
              label="Bid Total (USD)"
              value={data.bid3Total}
              onChange={handleChange('bid3Total')}
              variant="outlined"
              disabled={disabled}
            />
          </Box>
        </Box>

        <Alert severity="info">
          NOTE: If fewer than three bids were obtained please add your justification in box 4.
          (If competitive bids were not obtained, please explain why in box 4)
        </Alert>

        <FormControl component="fieldset" sx={{ mt: 2 }} disabled={disabled}>
          <FormLabel component="legend">
            Is the selected vendor the lowest total cost bid?
          </FormLabel>
          <RadioGroup
            value={data.isLowestBid}
            onChange={handleChange('isLowestBid')}
            onFocus={(e) => e.preventDefault()}
          >
            <FormControlLabel value="YES" control={<Radio />} label="YES" />
            <FormControlLabel value="NO" control={<Radio />} label="NO" />
          </RadioGroup>
          {data.isLowestBid === 'NO' && (
            <Alert severity="info" sx={{ mt: 1 }}>
              Please explain in Question 4 why a higher-cost vendor was selected.
            </Alert>
          )}
        </FormControl>

        <TextField
          required
          fullWidth
          multiline
          rows={4}
          label="Please document why the third party was selected"
          value={data.selectionReason}
          onChange={handleChange('selectionReason')}
          variant="outlined"
          disabled={disabled}
          error={!!getFieldError('selectionReason')}
          helperText={getFieldError('selectionReason')}
        />
      </Box>
    </Box>
  );
};

export default BidsForm;
